import { Injectable, inject, computed, DestroyRef, Signal } from '@angular/core';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ApolloQueryResult } from '@apollo/client/core';

import { GetLogoDesignConfigGQL, GetLogoDesignConfigQuery } from './graphql/getLogoDesignConfig.query.sitos-x-api.g';
import { DefaultUnitGQL } from './graphql/getDefaultUnit.sitos-x-api.g';
import { ImageReader } from './imageReader.service';

import { DataService, DataError, type PollStart } from './data.service';

/** Branding-Daten der Default-Unit, wie sie der Service bereitstellt. */
export interface CustomerBranding {
  /** Object-URL (blob:) des Logos; leer, wenn keines konfiguriert/geladen ist. */
  readonly logoUrl: string;
  /** Name der Default-Unit (units.defaultUnit.name). */
  readonly name: string;
}

/**
 * Kunden-Branding (Logo und Name der Default-Unit) als pollbare Datenquelle.
 *
 * Laedt die Logo-Quelle per GraphQL und das Bild selbst per Bearer-Token (ImageReader)
 * als Blob, der als Object-URL (String) bereitgestellt wird. Gekapselt im etablierten
 * DataService-Muster (vgl. DashboardStatisticsService). Damit kommt auch das Pausieren
 * des Pollings bei verstecktem Tab aus dem DataService (visibilitychange in
 * startPolling); das Stoppen beim Verlassen der Seite uebernimmt der Consumer via
 * stopPolling().
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerLogoService {

  readonly #debug: boolean = false;

  // Dependency Injection
  private readonly destroyRef = inject(DestroyRef);
  private readonly getLogoDesignConfigGQL = inject(GetLogoDesignConfigGQL);
  private readonly getDefaultUnitGQL = inject(DefaultUnitGQL);
  private readonly imageReader = inject(ImageReader);

  // Internal data service
  private readonly dataService!: DataService<CustomerBranding>;

  // Zuletzt geladenes Logo: dessen Bytes und die daraus erzeugte Object-URL.
  // Dient dem Polling: sind die Bytes unverändert, wird dieselbe Object-URL
  // zurückgegeben. So flackert das <img> nicht und es entsteht pro Tick kein neuer
  // Blob (Leak).
  //
  // Verglichen wird bewusst der INHALT, nicht die Quell-URL: beim Austausch des
  // Mandanten-Logos bleibt die URL i. d. R. gleich (stabiler Pfad zur Unit). Ein
  // URL-Vergleich hat den Re-Download deshalb dauerhaft übersprungen und der Header
  // zeigte weiter das alte Logo.
  #logoBytes: Uint8Array | null = null;
  #logoObjectUrl: string | null = null;

  constructor() {
    this.dataService = new DataService<CustomerBranding>(
      {
        name: 'CustomerLogoService', // Log-Praefix, siehe DataServiceConfig.name
        cacheEnabled: false,
        cacheDuration: 5 * 60 * 1000, // 5 minutes
        requestTimeout: 15000, // 15s – passt zum 60s-Poll (Worst Case inkl. Retries < 60s)
        // Intervall der Poll-Ticks. Entspricht dem DataService-Default, steht hier
        // aber bewusst explizit: er ist der GEGENSTUECK-Wert zu
        // #customerLogoPollIntervalMs in der NavigationMenuComponent, die nur die
        // Verzoegerung des ERSTEN Ticks setzt. Beide muessen zusammen geaendert
        // werden, sonst laufen erster Tick und Takt auseinander.
        pollInterval: 60000,
        maxRetries: 2,
        enableBackgroundRefresh: false, // inert bei cacheEnabled:false (Pattern B)
      },
      this.destroyRef
    );
  }

  /**
   * Das aktuelle Kundenlogo als Object-URL (blob:), leer solange keines geladen ist
   * oder keines konfiguriert wurde. Bleibt der Bild-INHALT über die Poll-Ticks gleich,
   * bleibt auch dieser String stabil – das <img> lädt dann nicht neu.
   */
  readonly customerLogo = computed<string>(() => this.dataService.data()[0]?.logoUrl ?? '');

  /**
   * Name der Default-Unit (Mandant), leer solange nichts geladen ist. Kommt aus
   * derselben Query wie das Logo – kein zusaetzlicher Request.
   */
  readonly customerName = computed<string>(() => this.dataService.data()[0]?.name ?? '');

  get loading(): Signal<boolean> {
    return this.dataService.loading;
  }

  get error(): Signal<DataError | null> {
    return this.dataService.error;
  }

  get lastUpdated(): Signal<number | null> {
    return this.dataService.lastUpdated;
  }

  /**
   * Lädt das Kundenlogo.
   *
   * @param forceRefresh - Force cache refresh
   */
  loadCustomerLogo(forceRefresh: boolean = false): void {
    this.dataService.load(() => this.fetchCustomerLogo(), forceRefresh);
  }

  /** Erzwingt ein Neuladen. */
  refresh(): void {
    this.dataService.forceRefresh(() => this.fetchCustomerLogo());
  }

  /**
   * Startet das Hintergrund-Polling (delegiert an DataService, Pattern B).
   * @param start Startzeitpunkt des ersten Ticks: 0 (Default) = sofort laden, n ms,
   *   oder 'interval' = erst mit dem naechsten regulaeren Tick (s. PollStart). Das
   *   Intervall selbst steht in der DataService-Config oben (pollInterval).
   */
  startPolling(start: PollStart = 0): void {
    this.dataService.startPolling(() => this.fetchCustomerLogo(), start);
  }

  /** Stoppt das Hintergrund-Polling. */
  stopPolling(): void {
    this.dataService.stopPolling();
  }

  /** Leert Cache und State und gibt die Object-URL des Logos frei. */
  clearCache(): void {
    this.dataService.clearCache();
    this.#releaseLogoObjectUrl();
  }

  // ========================================================================
  // Private Methods
  // ========================================================================

  /**
   * Ermittelt Name und Logo-Quelle der Default-Unit (Fallback fuer das Logo: iconUrl
   * aus der Config) und lädt das Bild als Blob nach. Ohne konfiguriertes Logo bleibt
   * logoUrl leer, der Name wird trotzdem geliefert.
   *
   * Single-Shot: fetch() + take(1) emittieren pro Aufruf genau einmal (auch bei
   * identischem Ergebnis) und sind damit fürs Polling geeignet – anders als
   * watch().valueChanges.
   */
  private fetchCustomerLogo(): Observable<CustomerBranding[]> {
    return combineLatest([
      this.getDefaultUnitGQL
        .fetch({}, { context: { client: 'sitosxapi' }, fetchPolicy: 'no-cache' })
        .pipe(map(response => ({
          logoUrl: response.data.units?.defaultUnit?.logoUrl ?? null,
          name: response.data.units?.defaultUnit?.name ?? '',
        }))),
      this.getLogoDesignConfigGQL
        .fetch({}, { context: { client: 'sitosxapi' }, fetchPolicy: 'no-cache' })
        .pipe(map((res: ApolloQueryResult<GetLogoDesignConfigQuery>) =>
          res.data.configs?.configData?.iconUrl ?? null)),
    ]).pipe(
      take(1),
      switchMap(([defaultUnit, iconUrl]) => {
        const name = defaultUnit.name;
        const logoUrl = defaultUnit.logoUrl ?? iconUrl ?? '';
        if (this.#debug) console.log('[CustomerLogoService] fetchCustomerLogo, name=', name, ', logoUrl=', logoUrl);

        // Kein Logo konfiguriert -> vorhandene Object-URL freigeben, Name bleibt.
        if (!logoUrl) {
          this.#releaseLogoObjectUrl();
          return of<CustomerBranding[]>([{ logoUrl: '', name }]);
        }

        // Bild bei JEDEM Tick laden und den Inhalt vergleichen (s. #logoBytes):
        // die Quell-URL bleibt beim Logo-Tausch gleich, taugt also nicht als
        // Aktualitaetskriterium.
        return this.imageReader.getImage(logoUrl).pipe(
          switchMap((blob: Blob) => from(blob.arrayBuffer()).pipe(
            map((buffer: ArrayBuffer) => {
              const bytes = new Uint8Array(buffer);

              // Inhalt unveraendert -> bestehende Object-URL behalten. Der
              // gelieferte String bleibt stabil, das <img> laedt nicht neu.
              if (this.#logoObjectUrl && this.#bytesEqual(this.#logoBytes, bytes)) {
                if (this.#debug) console.log('[CustomerLogoService] Logo unveraendert (', bytes.length, 'Bytes )');
                return [{ logoUrl: this.#logoObjectUrl, name }];
              }

              this.#releaseLogoObjectUrl();
              const objectUrl = URL.createObjectURL(blob);
              this.#logoObjectUrl = objectUrl;
              this.#logoBytes = bytes;
              if (this.#debug) console.log('[CustomerLogoService] Logo NEU geladen (', bytes.length, 'Bytes ) ->', objectUrl);
              return [{ logoUrl: objectUrl, name }];
            }),
          )),
        );
      }),
      // KEIN catchError hier: Fehler propagieren unveraendert (Default-Verhalten).
      // Der DataService setzt loading/error und loggt sie GENAU EINMAL; der
      // naechste Poll-Tick laedt unabhaengig neu (Polling stirbt NICHT).
    );
  }

  /**
   * Vergleicht die Bytes zweier Logo-Ladungen. Laenge zuerst – bei einem
   * ausgetauschten Logo unterscheidet die sich praktisch immer, der Byte-Loop
   * laeuft also selten ueberhaupt durch.
   */
  #bytesEqual(previous: Uint8Array | null, current: Uint8Array): boolean {
    if (!previous || previous.length !== current.length) return false;
    for (let i = 0; i < previous.length; i++) {
      if (previous[i] !== current[i]) return false;
    }
    return true;
  }

  /** Gibt die zuletzt erzeugte Object-URL frei (verhindert Blob-Leaks beim Polling). */
  #releaseLogoObjectUrl(): void {
    if (this.#logoObjectUrl) {
      URL.revokeObjectURL(this.#logoObjectUrl);
    }
    this.#logoObjectUrl = null;
    this.#logoBytes = null;
  }
}
